import { useState, useCallback, useMemo } from 'react';
import { Loader2, ClipboardList, Square } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { GuidedStepBar } from './GuidedPhaseBar';
import { GuidedWelcome } from './GuidedWelcome';
import { ClarificationControls } from './ClarificationControls';
import { PlanReview } from './PlanReview';
import { AssumptionReview } from './AssumptionReview';
import { StepExecutionPanel } from './StepExecutionPanel';
import { CompletionBanner } from './CompletionBanner';
import { WizardStepWithChat } from './WizardStepWithChat';
import { ConversationPane } from '../ConversationPane';
import { phaseToWizardStep } from '../../types/guided-builder';
import type { BuilderMessage } from '@/lib/api';
import type { VerboseEvent } from '../../hooks/useBuilderSession';
import type { GuidedPhase, GuidedFlow, BuildPlan } from '../../types/guided-builder';

interface GuidedWizardProps {
  guidedPhase: GuidedPhase;
  guidedFlow?: GuidedFlow;
  buildPlan?: BuildPlan;
  messages: BuilderMessage[];
  isSending: boolean;
  streamingText?: Record<string, string>;
  suggestedNextSteps?: string[];
  verbose?: boolean;
  verboseHistory?: Record<string, VerboseEvent[]>;
  pendingVerboseEvents?: VerboseEvent[];

  // Callbacks
  onGuidedStart: (idea: string, name?: string, description?: string) => void;
  onBrowseTemplates: () => void;
  onSubmitAnswers: (answers: Record<number, string>) => void;
  onPlanReviewApprove: () => void;
  onPlanReviewChanges: (feedback: string) => void;
  onSuggestAssumptionChanges: (comments: Record<string, string>) => void;
  onApprovePlan: (comments?: Record<string, string>) => void;
  onExecutionFeedback: (message: string) => void;
  onStopGeneration: () => void;
  onContinueChat: () => void;
  onSuggestionClick: (suggestion: string) => void;

  // Execution controls
  autoApprove: boolean;
  onAutoApproveChange: (value: boolean) => void;

  // For inline chat in steps 1-3
  onSendMessage: (message: string) => void;
}

export function GuidedWizard({
  guidedPhase,
  guidedFlow,
  buildPlan,
  messages,
  isSending,
  streamingText,
  suggestedNextSteps,
  verbose,
  verboseHistory,
  pendingVerboseEvents,
  onGuidedStart,
  onBrowseTemplates,
  onSubmitAnswers,
  onPlanReviewApprove,
  onPlanReviewChanges,
  onSuggestAssumptionChanges,
  onApprovePlan,
  onExecutionFeedback,
  onStopGeneration,
  onContinueChat,
  onSuggestionClick,
  autoApprove,
  onAutoApproveChange,
  onSendMessage,
}: GuidedWizardProps) {
  const currentStep = phaseToWizardStep(guidedPhase);
  const [viewingStep, setViewingStep] = useState<number | null>(null);
  const [chatOpen, setChatOpen] = useState(false);

  const handleStepClick = useCallback(
    (stepIndex: number) => {
      if (stepIndex === currentStep) {
        // Clicking active step clears backward navigation
        setViewingStep(null);
      } else if (stepIndex < currentStep) {
        setViewingStep(stepIndex);
      }
    },
    [currentStep],
  );

  const clearViewingStep = useCallback(() => {
    setViewingStep(null);
  }, []);

  // Handle "Request Changes" / "Suggest Changes" opening inline chat
  const handlePlanChangesWithChat = useCallback(
    (feedback: string) => {
      setChatOpen(true);
      onPlanReviewChanges(feedback);
    },
    [onPlanReviewChanges],
  );

  const handleAssumptionChangesWithChat = useCallback(
    (comments: Record<string, string>) => {
      setChatOpen(true);
      onSuggestAssumptionChanges(comments);
    },
    [onSuggestAssumptionChanges],
  );

  // Determine what to display
  const displayStep = viewingStep ?? currentStep;
  const isViewingPast = viewingStep !== null && viewingStep < currentStep;
  const clarifyingQA = guidedFlow?.clarifyingQA ?? [];
  const assumptions = guidedFlow?.assumptions ?? [];

  // Plan generating state
  const isPlanGenerating = guidedPhase === 'planning' && !buildPlan && isSending;

  // Guided content for ConversationPane in Build step
  const guidedHeaderContent = useMemo(() => {
    if (guidedPhase === 'complete') {
      return <CompletionBanner buildPlan={buildPlan ?? null} onContinueChat={onContinueChat} />;
    }
    return null;
  }, [guidedPhase, buildPlan, onContinueChat]);

  const guidedFooterContent = useMemo(() => {
    if (guidedPhase === 'execution' && buildPlan?.approved) {
      return (
        <StepExecutionPanel
          buildPlan={buildPlan}
          autoApprove={autoApprove}
          onAutoApproveChange={onAutoApproveChange}
          onStopGeneration={onStopGeneration}
          onSendFeedback={onExecutionFeedback}
          isProcessing={isSending}
        />
      );
    }
    return null;
  }, [
    guidedPhase,
    buildPlan,
    autoApprove,
    onAutoApproveChange,
    onStopGeneration,
    onExecutionFeedback,
    isSending,
  ]);

  const renderStepContent = () => {
    // Step 0: Describe (ideation)
    if (displayStep === 0) {
      if (isViewingPast) {
        return (
          <div className="flex-1 flex flex-col items-center justify-center p-6 gap-4 text-center">
            <p className="text-sm text-muted-foreground">
              Idea: {guidedFlow?.ideaSummary ?? 'No idea recorded'}
            </p>
          </div>
        );
      }
      return (
        <GuidedWelcome
          onStart={onGuidedStart}
          onBrowseTemplates={onBrowseTemplates}
          isProcessing={isSending}
        />
      );
    }

    // Step 1: Clarify (clarification)
    if (displayStep === 1) {
      const panel = (
        <ClarificationControls
          clarifyingQA={clarifyingQA}
          onSubmitAnswers={onSubmitAnswers}
          isProcessing={isViewingPast || isSending}
        />
      );

      if (!panel) {
        return (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        );
      }

      return (
        <WizardStepWithChat
          messages={messages}
          isProcessing={isSending}
          streamingText={streamingText}
          onSendMessage={onSendMessage}
          guidedPhase={guidedPhase}
          chatOpen={chatOpen}
          onChatOpenChange={setChatOpen}
          verbose={verbose}
          verboseHistory={verboseHistory}
          pendingVerboseEvents={pendingVerboseEvents}
        >
          {panel}
        </WizardStepWithChat>
      );
    }

    // Step 2: Plan (planning)
    if (displayStep === 2) {
      let panel: React.ReactNode;

      if (isPlanGenerating && !isViewingPast) {
        panel = (
          <div className="flex-1 flex flex-col p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="relative flex-shrink-0">
                <ClipboardList className="h-8 w-8 text-muted-foreground/40" />
                <Loader2 className="h-4 w-4 animate-spin text-primary absolute -bottom-1 -right-1" />
              </div>
              <div>
                <h3 className="text-base font-semibold">Generating your build plan...</h3>
                <p className="text-sm text-muted-foreground">
                  Researching patterns and designing the steps for your arche.
                </p>
              </div>
            </div>
            {streamingText?.['builder'] && (
              <div className="flex-1 rounded-md bg-muted/30 p-4 overflow-y-auto">
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                  {streamingText['builder']}
                </p>
              </div>
            )}
          </div>
        );
      } else if (buildPlan) {
        panel = (
          <PlanReview
            buildPlan={buildPlan}
            onApprove={onPlanReviewApprove}
            onRequestChanges={handlePlanChangesWithChat}
            isProcessing={isViewingPast || isSending}
          />
        );
      } else {
        panel = (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        );
      }

      return (
        <WizardStepWithChat
          messages={messages}
          isProcessing={isSending}
          streamingText={streamingText}
          onSendMessage={onSendMessage}
          guidedPhase={guidedPhase}
          chatOpen={chatOpen}
          onChatOpenChange={setChatOpen}
          verbose={verbose}
          verboseHistory={verboseHistory}
          pendingVerboseEvents={pendingVerboseEvents}
        >
          {panel}
        </WizardStepWithChat>
      );
    }

    // Step 3: Review (assumptions)
    if (displayStep === 3) {
      const panel =
        assumptions.length > 0 || buildPlan ? (
          <AssumptionReview
            assumptions={assumptions}
            buildPlan={buildPlan ?? null}
            onSuggestChanges={handleAssumptionChangesWithChat}
            onApprovePlan={onApprovePlan}
            isProcessing={isViewingPast || isSending}
          />
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        );

      return (
        <WizardStepWithChat
          messages={messages}
          isProcessing={isSending}
          streamingText={streamingText}
          onSendMessage={onSendMessage}
          guidedPhase={guidedPhase}
          chatOpen={chatOpen}
          onChatOpenChange={setChatOpen}
          verbose={verbose}
          verboseHistory={verboseHistory}
          pendingVerboseEvents={pendingVerboseEvents}
        >
          {panel}
        </WizardStepWithChat>
      );
    }

    // Step 4: Build (execution + complete)
    return (
      <ConversationPane
        messages={messages}
        isProcessing={isSending}
        suggestedNextSteps={suggestedNextSteps}
        streamingText={streamingText}
        verboseHistory={verbose ? verboseHistory : undefined}
        pendingVerboseEvents={verbose ? pendingVerboseEvents : undefined}
        onSuggestionClick={onSuggestionClick}
        headerContent={guidedHeaderContent}
        footerContent={guidedFooterContent}
        className="flex-1 min-h-0"
      />
    );
  };

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Step bar — shown when guided flow is active */}
      {guidedFlow && (
        <GuidedStepBar
          currentPhase={guidedPhase}
          buildPlan={buildPlan}
          viewingStep={viewingStep}
          onStepClick={handleStepClick}
        />
      )}

      {/* "Back to current step" pill when viewing a past step */}
      {isViewingPast && (
        <div className="flex justify-center py-1.5 bg-muted/20 border-b flex-shrink-0">
          <button
            type="button"
            onClick={clearViewingStep}
            className="text-xs text-primary hover:text-primary/80 bg-primary/10 hover:bg-primary/20 px-3 py-1 rounded-full transition-colors"
          >
            Back to current step
          </button>
        </div>
      )}

      {/* Step content */}
      {renderStepContent()}

      {/* Stop button for steps 1-3 when processing */}
      {displayStep >= 1 && displayStep <= 3 && isSending && !isViewingPast && (
        <div className="flex items-center justify-center px-4 py-2 border-t bg-muted/20 flex-shrink-0">
          <Button onClick={onStopGeneration} variant="destructive" size="sm" className="gap-2">
            <Square className="h-3.5 w-3.5" />
            Stop
          </Button>
        </div>
      )}
    </div>
  );
}
